import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import Layout from '../../layout/Layout';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Pagination from '../../common/Pagination';
import supplierService, { 
  SupplierBase, 
  Supplier as ApiSupplier,
  SupplierPricingConfig
} from '../../../services/supplierService';
import inventoryService from '../../../services/inventoryService';
import backButtonIcon from '../../../assets/backButton.png';
import customerAddIcon from '../../../assets/customerAddIcon.png';
import { useToast } from '../../../hooks/useToast';
import { Toast } from '../../common/Toast';
import LoadingSpinner from '../../ui/LoadingSpinner';
import SearchBar from '../../common/SearchBar';
import { API_URL } from '../../../config';
import DuplicateSkuModal from '../../common/DuplicateSkuModal';
import ImportResultModal from '../../common/ImportResultModal';
import { 
  generateInventoryTemplate, 
  generatePriceTemplate, 
  generatePriceListConfigurationTemplate 
} from '../../../utils/excelTemplateGenerator';
import { 
  readExcelFile, 
  excelDataToCsvFormat, 
  excelDataToCsvFormatWithMapping,
  validateExcelHeaders,
  processPriceConfigExcel 
} from '../../../utils/excelReader';

// Custom styled search bar wrapper for this page
const StyledSearchBar = styled(SearchBar)`
  width: 300px;
  flex-shrink: 0;
  flex-grow: 1;
  max-width: 500px;
  
  /* Target the input inside the SearchBar */
  & input {
    height: 36px;
    border-radius: 8px;
    border: 1px solid #E5E7EB;
    font-size: 14px;
    padding: 8px 12px 8px 2.5rem;
  }
  
  /* Ensure the search icon is properly positioned */
  & > div:first-child {
    left: 1rem;
  }
`;

// Replace the StyledSearchBar with a simple custom search input
const SearchInput = styled.input`
  width: 300px;
  flex-shrink: 0;
  flex-grow: 1;
  max-width: 500px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #E5E7EB;
  font-size: 14px;
  padding: 8px 12px 8px 2.5rem;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z'/%3e%3c/svg%3e");
  background-position: left 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1rem 1rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3B82F6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9CA3AF;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    font-size: 16px; /* Prevents zoom on mobile */
    min-width: 0;
  }
  
  @media (max-width: 480px) {
    height: 44px;
    padding: 12px 16px 12px 3rem;
  }
`;

const PageHeader = styled.div`
  padding: 0.5rem 1rem 0;
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem 0;
  }
  
  @media (max-width: 480px) {
    padding: 0.25rem 0.5rem 0;
  }
`;

const PageTitle = styled.h1`
  color: #042B41;
  font-size: 32px;
  font-weight: 800;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
  
  @media (max-width: 480px) {
    font-size: 24px;
  }
  
  @media (max-height: 600px) {
    font-size: 24px;
  }
`;

const PageContainer = styled.div`
  padding: 0 1rem 0.5rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-height: calc(100vh - 60px);
  overflow-x: auto;
  
  @media (max-width: 768px) {
    padding: 0 0.75rem 0.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 0.5rem 0.5rem;
  }
`;

const SearchAddContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  width: 100%;
  gap: 12px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const StyledBackButton = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #042B41;
  flex-shrink: 0;
`;

const BackIcon = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 8px;
  
  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
    margin-right: 6px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const AddSupplierButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: #042B41;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  height: 36px;
  min-width: 120px;
  box-sizing: border-box;
  
  &:hover {
    background-color: #0A3D5A;
  }
  
  img {
    width: 16px;
    height: 16px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    height: 44px;
    font-size: 16px;
    padding: 12px 16px;
  }
  
  @media (max-width: 480px) {
    min-width: unset;
    gap: 6px;
  }
`;

const GridContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid #e0e0e0;
  overflow-x: auto;
  min-width: 0;
  position: relative;
  
  /* Add table-like borders to all cells */
  & > div:not(:first-child) {
    border-top: 1px solid #E5E7EB;
  }
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  
  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
  
  @media (max-width: 768px) {
    border-radius: 6px;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    border-radius: 4px;
  }
`;

const GridHeader = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr;
  background-color: #042B41;
  color: white;
  border-bottom: 1px solid #E5E7EB;
  min-width: 800px;

  div {
    padding: 1rem;
    font-weight: 700;
    font-size: 14px;
    border-right: 1px solid rgba(255, 255, 255, 0.2);
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;

    &:last-child {
      border-right: none;
    }
  }
  
  @media (max-width: 768px) {
    min-width: 600px;
    
    div {
      padding: 0.75rem;
      font-size: 12px;
    }
  }
  
  @media (max-width: 480px) {
    min-width: 500px;
    
    div {
      padding: 0.5rem;
      font-size: 11px;
    }
  }
  
  @media (max-height: 600px) {
    div {
      padding: 0.75rem;
      font-size: 12px;
    }
  }
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr;
  border-bottom: 1px solid #E5E7EB;
  min-width: 800px;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #F9FAFB;
  }

  div {
    padding: 1rem;
    font-size: 14px;
    color: #111827;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    border-right: 1px solid #E5E7EB;
    border-bottom: 1px solid #E5E7EB;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;

    &:last-child {
      border-right: none;
    }
  }
  
  @media (max-width: 768px) {
    min-width: 600px;
    
    div {
      padding: 0.75rem;
      font-size: 12px;
    }
  }
  
  @media (max-width: 480px) {
    min-width: 500px;
    
    div {
      padding: 0.5rem;
      font-size: 11px;
    }
  }
  
  @media (max-height: 600px) {
    div {
      padding: 0.75rem;
      font-size: 12px;
    }
  }
`;

const SupplierNameLink = styled(Link)`
  color: #0056b3;
  text-decoration: underline;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    color: #003d7a;
    text-decoration: underline;
  }
`;

interface Supplier {
  id: number;
  name: string;
  phone: string;
  email: string;
  address: string;
  priceList: string;
}

// Add Supplier Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    align-items: flex-start;
    padding-top: 2rem;
    padding-bottom: 2rem;
  }
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 600px;
  max-height: calc(100vh - 2rem);
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  margin: auto;
  position: relative;
  
  @media (max-width: 768px) {
    max-width: 100%;
    max-height: calc(100vh - 4rem);
    padding: 1.5rem;
    margin: 0;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 6px;
  }
  
  /* Ensure content doesn't get cut off on small screens */
  @media (max-height: 700px) {
    max-height: calc(100vh - 1rem);
    padding: 1rem;
  }
  
  @media (max-height: 600px) {
    max-height: calc(100vh - 0.5rem);
    padding: 0.75rem;
  }
`;

const SubmittingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.75);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
`;

const SubmittingText = styled.p`
  margin-top: 1rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.95rem;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
  
  @media (max-height: 600px) {
    margin-bottom: 1rem;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  color: #111827;
  margin: 0;
  font-weight: 600;
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
  
  @media (max-height: 600px) {
    font-size: 1.25rem;
  }
`;

const TemplateLink = styled.a`
  color: #2563EB;
  font-size: 0.875rem;
  text-decoration: none;
  display: flex;
  align-items: center;

  &:hover {
    text-decoration: underline;
  }

  svg {
    margin-right: 0.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  @media (max-width: 480px) {
    margin-bottom: 1rem;
  }
  
  @media (max-height: 600px) {
    margin-bottom: 1rem;
  }
`;

const FormLabel = styled.label<{ required?: boolean }>`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  margin-bottom: 0.5rem;

  &::after {
    content: ${props => props.required ? '"*"' : '""'};
    color: #EF4444;
    margin-left: 2px;
  }
`;

// Add new styled component for form labels with template links
const FormLabelWithTemplate = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const FormLabelText = styled.label<{ required?: boolean }>`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;

  &::after {
    content: ${props => props.required ? '"*"' : '""'};
    color: #EF4444;
    margin-left: 2px;
  }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.625rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #2563EB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    font-size: 0.8rem;
  }
  
  @media (max-height: 600px) {
    padding: 0.5rem;
  }
`;

const FileUploadContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  
  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const FileInputLabel = styled.label`
  flex: 1;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  padding: 0.625rem;
  font-size: 0.875rem;
  color: #6B7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  background-color: #F9FAFB;
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    font-size: 0.8rem;
  }
  
  @media (max-height: 600px) {
    padding: 0.5rem;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadIcon = styled.span`
  color: #6B7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1rem;
  }
  
  @media (max-height: 600px) {
    margin-top: 1rem;
    gap: 0.75rem;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  background-color: white;
  color: #111827;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  min-height: 44px;
  box-sizing: border-box;

  &:hover {
    background-color: #F9FAFB;
  }
  
  @media (max-width: 480px) {
    padding: 0.625rem;
    font-size: 0.8rem;
    min-height: 40px;
  }
  
  @media (max-height: 600px) {
    padding: 0.625rem;
    min-height: 40px;
  }
`;

const SubmitButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 0.375rem;
  background-color: #042B41;
  color: white;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  min-height: 44px;
  box-sizing: border-box;

  &:hover {
    background-color: #031F30;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  @media (max-width: 480px) {
    padding: 0.625rem;
    font-size: 0.8rem;
    min-height: 40px;
  }
  
  @media (max-height: 600px) {
    padding: 0.625rem;
    min-height: 40px;
  }
`;

const ErrorMessage = styled.div`
  color: #EF4444;
  background-color: #FEE2E2;
  border: 1px solid #FCA5A5;
  padding: 0.75rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.div`
  color: #059669;
  background-color: #D1FAE5;
  border: 1px solid #6EE7B7;
  padding: 0.75rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
`;

// Remove skeleton components and add consistent loading components
const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  border-bottom: 1px solid #E5E7EB;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const LoadingText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6B7280;
  font-weight: 500;
`;

const MessageRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  border-bottom: 1px solid #E5E7EB;
  color: #6B7280;
  font-size: 14px;
  text-align: center;
`;

const ScrollHint = styled.div`
  background-color: #F9FAFB;
  border-top: 1px solid #E5E7EB;
  padding: 8px 16px;
  text-align: center;
  font-size: 12px;
  color: #6B7280;
  display: none;
  
  @media (max-width: 1200px) {
    display: block;
  }
`;

// Validation Error Alert Modal Styled Components
const AlertModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
`;

const AlertModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 400px;
  padding: 1.25rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 480px) {
    padding: 1rem;
    max-width: 100%;
  }
`;

const AlertModalTitle = styled.h2`
  font-size: 1.125rem;
  color: #DC2626;
  margin: 0 0 0.75rem 0;
  font-weight: 600;
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const AlertModalMessage = styled.div`
  color: #111827;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  word-wrap: break-word;
  max-height: 200px;
  overflow-y: auto;
  padding-right: 0.5rem;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const AlertButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.75rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const AlertCancelButton = styled.button`
  padding: 0.625rem 1.25rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  background-color: white;
  color: #111827;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  min-height: 36px;
  
  &:hover {
    background-color: #F9FAFB;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    padding: 0.625rem;
    min-height: 36px;
  }
`;

const SuppliersPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();

  // Initialize state from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const initialPage = parseInt(urlParams.get('page') || '1', 10);
  const initialLimit = parseInt(urlParams.get('limit') || '10', 10);
  const initialSearch = urlParams.get('search') || '';

  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);

  // Add Supplier Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSupplier, setNewSupplier] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    priceList: ''
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [processedPriceList, setProcessedPriceList] = useState<any[]>([]);
  const [selectedInventoryFile, setSelectedInventoryFile] = useState<File | null>(null);
  const [processedInventoryList, setProcessedInventoryList] = useState<any[]>([]);
  const [selectedPriceConfigFile, setSelectedPriceConfigFile] = useState<File | null>(null);
  const [processedPriceConfigList, setProcessedPriceConfigList] = useState<any[]>([]);
  
  // Duplicate SKU validation state
  const [showDuplicateSkuModal, setShowDuplicateSkuModal] = useState(false);
  const [duplicateSkus, setDuplicateSkus] = useState<string[]>([]);
  
  // File validation error alert modal state
  const [showValidationErrorModal, setShowValidationErrorModal] = useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState<string>('');

  // Import result modal state
  const [showImportResultModal, setShowImportResultModal] = useState(false);
  const [importResult, setImportResult] = useState<{
    successCount: number;
    errorCount: number;
    errorDetails: string[];
    supplierName: string;
  }>({
    successCount: 0,
    errorCount: 0,
    errorDetails: [],
    supplierName: ''
  });

  // API state
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  // Add lastFetchTime to track when the data was last fetched
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  // Add totalItems state to track total items from API response
  const [totalItems, setTotalItems] = useState(0);

  // Add a ref to track API calls in progress
  const apiCallInProgress = useRef<boolean>(false);

  // Add a ref to track if this is the initial load
  const initialLoad = useRef<boolean>(true);

  // Add a ref to track user-initiated search vs. programmatic search updates
  const userSearchAction = useRef<boolean>(false);

  // Function to update URL with current pagination state
  const updateURLParams = useCallback((page: number, limit: number, search: string) => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page.toString());
    if (limit !== 10) params.set('limit', limit.toString());
    if (search) params.set('search', search);

    const newURL = params.toString() ? `/suppliers?${params.toString()}` : '/suppliers';
    navigate(newURL, { replace: true });
  }, [navigate]);

  // Natural sorting function for supplier names
  const naturalSort = (suppliers: Supplier[]): Supplier[] => {
    return suppliers.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();

      // Check if both names follow "supplier X" pattern
      const supplierRegex = /^supplier\s+(\d+)$/;
      const matchA = nameA.match(supplierRegex);
      const matchB = nameB.match(supplierRegex);

      // If both are "supplier X" format, sort numerically
      if (matchA && matchB) {
        const numA = parseInt(matchA[1]);
        const numB = parseInt(matchB[1]);
        return numA - numB;
      }

      // If only A is "supplier X" format, A comes first
      if (matchA && !matchB) {
        return -1;
      }

      // If only B is "supplier X" format, B comes first
      if (!matchA && matchB) {
        return 1;
      }

      // If neither follows the pattern, sort alphabetically
      return nameA.localeCompare(nameB);
    });
  };

  // Function to fetch suppliers from API - memoized with useCallback
  const fetchSuppliers = useCallback(async () => {
    // Skip if an API call is already in progress
    if (apiCallInProgress.current) {
      console.log('Skipping fetch - API call already in progress');
      return;
    }

    apiCallInProgress.current = true;
    setLoading(true);
    try {
      // Build search parameters - ensure search query is properly formatted
      const searchParams = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        ...(searchQuery.trim() && { search: searchQuery.trim() })
      };

      // Use 'any' type to avoid type errors with the API client
      console.log('Fetching suppliers with params:', searchParams);
      const response: any = await supplierService.getSuppliers(searchParams);
      console.log('Suppliers API response received:', response);

      // Handle both response.data and response.items for compatibility
      const suppliersData = response.data || response.items || [];
      
      // Ensure data exists and is an array before mapping
      if (!Array.isArray(suppliersData)) {
        console.log('No valid data in response, setting empty suppliers. Response:', response);
        setSuppliers([]);
        setTotalItems(0);
        setDataLoaded(true);
        return;
      }

      // Convert API supplier model to component supplier model
      const mappedSuppliers: Supplier[] = suppliersData.map((supplier: any) => ({
        id: supplier.id,
        name: supplier.supplier_name || supplier.name || '',
        phone: supplier.phone_no || supplier.phone || '',
        email: supplier.email || '',
        address: supplier.address || '',
        priceList: (supplier.price_list && Array.isArray(supplier.price_list) && supplier.price_list.length > 0) ? 'Available' : 'Not available'
      }));

      console.log('Mapped suppliers:', mappedSuppliers.length, 'suppliers');
      setSuppliers(naturalSort(mappedSuppliers));
      // Set total count from API response if available
      setTotalItems(response.total || mappedSuppliers.length);
      setDataLoaded(true);
      setLastFetchTime(Date.now());
      
      // Log for debugging
      if (mappedSuppliers.length === 0 && response.total === 0) {
        console.log('No suppliers found in database');
      }
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      toast.showToast('Failed to load suppliers. Please try again.', { type: 'error' });

      // Keep the existing data if available, but still mark as loaded
      setSuppliers(prevSuppliers => prevSuppliers.length > 0 ? prevSuppliers : []);
      setTotalItems(0);
      setDataLoaded(true);
    } finally {
      setLoading(false);
      apiCallInProgress.current = false;
    }
  }, [currentPage, itemsPerPage, searchQuery]);

  // Fetch suppliers on component mount and when dependencies change
  useEffect(() => {
    // If an API call is already in progress, don't start another one
    if (apiCallInProgress.current) return;

    // Use requestAnimationFrame to avoid issues with React 18 strict mode
    const rafId = requestAnimationFrame(() => {
      fetchSuppliers();
    });

    return () => cancelAnimationFrame(rafId);
  }, [fetchSuppliers]);

  // Simple search effect that only resets page for user-initiated searches
  useEffect(() => {
    // Skip on initial load
    if (initialLoad.current) {
      initialLoad.current = false;
      console.log('SuppliersPage: Initial load, not triggering search reset');
      return;
    }

    // Only reset page if this was a user-initiated search action
    if (userSearchAction.current) {
      console.log('SuppliersPage: User search detected, resetting to page 1');
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      userSearchAction.current = false; // Reset the flag
    }

    // Always update URL to keep it in sync (after a short delay)
    const timer = setTimeout(() => {
      updateURLParams(currentPage, itemsPerPage, searchQuery);
    }, 100);

    return () => clearTimeout(timer);
  }, [searchQuery]); // Only trigger on search query changes

  // Calculate pagination - use total from API instead of local length
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    updateURLParams(pageNumber, itemsPerPage, searchQuery);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
    updateURLParams(1, newItemsPerPage, searchQuery);
  };

  const handleBack = () => {
    navigate('/home');
  };

  const handleSupplierClick = (supplierId: number) => {
    // Preserve current pagination state in the URL
    const currentParams = new URLSearchParams();
    currentParams.set('page', currentPage.toString());
    currentParams.set('limit', itemsPerPage.toString());
    if (searchQuery) {
      currentParams.set('search', searchQuery);
    }

    navigate(`/suppliers/${supplierId}?${currentParams.toString()}`);
  };

  const handleSearchChange = (newSearchQuery: string) => {
    userSearchAction.current = true; // Mark this as a user-initiated search
    setSearchQuery(newSearchQuery);
  };

  const handleAddSupplierClick = () => {
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewSupplier({
      name: '',
      phone: '',
      email: '',
      address: '',
      priceList: ''
    });
    setSelectedFile(null);
    setProcessedPriceList([]);
    setSelectedInventoryFile(null);
    setProcessedInventoryList([]);
    setSelectedPriceConfigFile(null);
    setProcessedPriceConfigList([]);
  };

  const handleCloseValidationErrorModal = () => {
    setShowValidationErrorModal(false);
    setValidationErrorMessage('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewSupplier(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      setSelectedFile(file);

      try {
        // Process Excel file
        const data = await readExcelFile(file);

        if (data.rows.length === 0) {
          setValidationErrorMessage('Price list file appears to be empty.');
          setShowValidationErrorModal(true);
          setSelectedFile(null);
          setProcessedPriceList([]);
          return;
        }

        // Validate required columns for price list - updated to match the project template
        // We use a more flexible check here to support both template and older versions
        const templateHeaders = ['SKU CODE', 'DESCRIPTION', 'STYLE CODE', 'RRP (EX GST)', 'BWA BUY PRICE 50% (EX GST)'];
        const legacyHeaders = ['SKU CODE', 'PRODUCT DESCRIPTION', 'STYLE CODE', 'RRP (EX GST)', 'BWA BUY PRICE -50% (EX GST)'];
        
        let validation = validateExcelHeaders(data, templateHeaders);
        
        // If template validation fails, try legacy headers
        if (!validation.valid) {
          const legacyValidation = validateExcelHeaders(data, legacyHeaders);
          if (legacyValidation.valid) {
            validation = legacyValidation;
          }
        }

        if (!validation.valid) {
          setValidationErrorMessage(`Invalid price list file. Missing required columns: ${validation.missingHeaders.join(', ')}.`);
          setShowValidationErrorModal(true);
          setSelectedFile(null);
          setProcessedPriceList([]);
          return;
        }

        // Convert to processable format using header mapping
        const processedData = excelDataToCsvFormatWithMapping(data, validation.headerMapping);

        // Validate that we have at least one valid row
        if (processedData.length === 0) {
          setValidationErrorMessage('Price list file is empty or contains no valid data rows.');
          setShowValidationErrorModal(true);
          setSelectedFile(null);
          setProcessedPriceList([]);
          return;
        }

        setProcessedPriceList(processedData);
        toast.showToast(`Price list file processed successfully. ${processedData.length} items found.`, { type: 'success' });
      } catch (error) {
        console.error('Error processing price list file:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        setValidationErrorMessage(`Error processing price list file: ${errorMessage}`);
        setShowValidationErrorModal(true);
        setSelectedFile(null);
        setProcessedPriceList([]);
      }
    }

    // Reset the input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleInventoryFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      setSelectedInventoryFile(file);

      try {
        // Process Excel file
        const data = await readExcelFile(file);

        if (data.rows.length === 0) {
          setValidationErrorMessage('Inventory file appears to be empty.');
          setShowValidationErrorModal(true);
          setSelectedInventoryFile(null);
          setProcessedInventoryList([]);
          return;
        }

        // Validate required columns
        const requiredColumns = ['SKU CODE', 'STYLE CODE'];
        const validation = validateExcelHeaders(data, requiredColumns);

        if (!validation.valid) {
          setValidationErrorMessage(`Invalid inventory file. Missing required columns: ${validation.missingHeaders.join(', ')}.`);
          setShowValidationErrorModal(true);
          setSelectedInventoryFile(null);
          setProcessedInventoryList([]);
          return;
        }

        // Convert to processable format using header mapping
        const processedData = excelDataToCsvFormatWithMapping(data, validation.headerMapping);

        // Validate that we have at least one valid row
        if (processedData.length === 0) {
          setValidationErrorMessage('Inventory file is empty or contains no valid data rows.');
          setShowValidationErrorModal(true);
          setSelectedInventoryFile(null);
          setProcessedInventoryList([]);
          return;
        }

        setProcessedInventoryList(processedData);
        toast.showToast(`Inventory file processed successfully. ${processedData.length} items found.`, { type: 'success' });
      } catch (error) {
        console.error('Error processing inventory file:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        setValidationErrorMessage(`Error processing inventory file: ${errorMessage}`);
        setShowValidationErrorModal(true);
        setSelectedInventoryFile(null);
        setProcessedInventoryList([]);
      }
    }

    // Reset the input value to allow selecting the same file again
    e.target.value = '';
  };

  const handlePriceConfigFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setSelectedPriceConfigFile(file);
      
      try {
        // Process the Excel file with automatic percentage conversion
        const data = await processPriceConfigExcel(file);
        setProcessedPriceConfigList(data);
        toast.showToast(`Price configuration loaded successfully. ${data.length} configuration(s) found.`, { type: 'success' });
        console.log('Processed price list configuration:', data);
      } catch (error) {
        console.error('Error processing price configuration file:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        setValidationErrorMessage(`Error processing price configuration file: ${errorMessage}`);
        setShowValidationErrorModal(true);
        setSelectedPriceConfigFile(null);
        setProcessedPriceConfigList([]);
      }
    } else {
      setSelectedPriceConfigFile(null);
      setProcessedPriceConfigList([]);
    }
  };

  const handleSubmit = async () => {
    // Create a new supplier using the API
    try {
      setIsSubmitting(true);
      setLoading(true);

      // Validate required fields
      if (!newSupplier.name || !newSupplier.phone || !selectedFile || !selectedInventoryFile) {
        throw new Error('Please fill all required fields including price list and inventory file');
      }

      // Cross-validation: Check SKU codes between inventory and price list
      const inventorySkuCodes = processedInventoryList.map(item => item['SKU CODE']).filter(Boolean);
      const priceListSkuCodes = processedPriceList.map(item => item['SKU CODE']).filter(Boolean);

      // ENHANCED: Check for duplicate SKUs in the database
      try {
        const duplicateCheckResult = await inventoryService.checkDuplicateSkus(selectedInventoryFile);
        if (duplicateCheckResult.duplicates && duplicateCheckResult.duplicates.length > 0) {
          // Show the duplicate SKU modal instead of throwing an error
          setDuplicateSkus(duplicateCheckResult.duplicates);
          setShowDuplicateSkuModal(true);
          setLoading(false);
          setIsSubmitting(false);
          return; // Stop the submission process
        }
      } catch (duplicateError) {
        console.error('Error checking for duplicate SKUs:', duplicateError);
        // Continue with submission if duplicate check fails
      }

      // Find matching and non-matching SKU codes
      const matchingSkuCodes = inventorySkuCodes.filter(sku => priceListSkuCodes.includes(sku));
      const inventoryOnlySkuCodes = inventorySkuCodes.filter(sku => !priceListSkuCodes.includes(sku));
      const priceListOnlySkuCodes = priceListSkuCodes.filter(sku => !inventorySkuCodes.includes(sku));

      // Filter processed data to only include matching SKU codes
      const validatedInventoryList = processedInventoryList.filter(item =>
        matchingSkuCodes.includes(item['SKU CODE'])
      );
      const validatedPriceList = processedPriceList.filter(item =>
        matchingSkuCodes.includes(item['SKU CODE'])
      );

      // Show validation results
      if (matchingSkuCodes.length === 0) {
        throw new Error('No matching SKU codes found between inventory and price list files. Please check your data.');
      }

      let validationMessage = `${matchingSkuCodes.length} SKU codes matched successfully.`;
      if (inventoryOnlySkuCodes.length > 0 || priceListOnlySkuCodes.length > 0) {
        const errorDetails = [];
        if (inventoryOnlySkuCodes.length > 0) {
          errorDetails.push(`${inventoryOnlySkuCodes.length} SKU codes in inventory not found in price list`);
        }
        if (priceListOnlySkuCodes.length > 0) {
          errorDetails.push(`${priceListOnlySkuCodes.length} SKU codes in price list not found in inventory`);
        }
        validationMessage += ` ${errorDetails.join(', ')}.`;
      }

      // Create supplier data object in API format
      const supplierData: SupplierBase = {
        supplier_name: newSupplier.name, // Will be mapped to 'name' in the backend
        phone_no: newSupplier.phone.replace(/[^\d+]/g, ''), // Remove all characters except digits and +
        email: newSupplier.email,
        address: newSupplier.address,
        // Map CSV columns to backend expected field names - handle both template and legacy headers
        price_list: validatedPriceList?.length ? validatedPriceList.map((item, index) => ({
          sku: {
            id: index + 1,
            code: item['SKU CODE'] || ''
          },
          // Try both 'DESCRIPTION' (template) and 'PRODUCT DESCRIPTION' (legacy)
          description: item['DESCRIPTION'] || item['PRODUCT DESCRIPTION'] || '',
          style_code: item['STYLE CODE'] || '',
          rrp_ex_gst: parseFloat(String(item['RRP (EX GST)'] || '0').replace(/[$,]/g, '')) || 0,
          // Try both 'BWA BUY PRICE 50% (EX GST)' (template) and 'BWA BUY PRICE -50% (EX GST)' (legacy)
          bwa_buy_price_50_ex_gst: parseFloat(String(item['BWA BUY PRICE 50% (EX GST)'] || item['BWA BUY PRICE -50% (EX GST)'] || '0').replace(/[$,]/g, '')) || 0
        })) : []
      };

      // Step 1: Create the supplier
      const createdSupplier = await supplierService.createSupplier(supplierData);
      
      // Step 1.5: Apply price configuration if provided
      if (processedPriceConfigList.length > 0) {
        try {
          const priceConfigData = processedPriceConfigList[0]; // Assuming single row of config data
          
          // Convert percentage values to markup multipliers
          const regionalRetailMarkup = 1 + (parseFloat(priceConfigData['Regional Retail AUD Incl']) || 60) / 100;
          const regionalTradeMarkup = 1 + (parseFloat(priceConfigData['Regional Trade AUD Incl']) || 50) / 100;
          const metroRetailMarkup = 1 + (parseFloat(priceConfigData['Metro Retail AUD Incl']) || 60) / 100;
          const metroTradeMarkup = 1 + (parseFloat(priceConfigData['Metro Trade AUD Incl']) || 50) / 100;
          const regionalRetailVipMarkup = 1 + (parseFloat(priceConfigData['Regional Retail VIP AUD Incl']) || 50) / 100;
          const regionalTradeVip1Markup = 1 + (parseFloat(priceConfigData['Regional Trade VIP1 AUD Incl']) || 45) / 100;
          const regionalTradeVip2Markup = 1 + (parseFloat(priceConfigData['Regional Trade VIP2 AUD Incl']) || 40) / 100;
          const metroRetailVipMarkup = 1 + (parseFloat(priceConfigData['Metro Retail VIP AUD Incl']) || 50) / 100;
          const metroTradeVip1Markup = 1 + (parseFloat(priceConfigData['Metro Trade VIP1 AUD Incl']) || 45) / 100;
          const metroTradeVip2Markup = 1 + (parseFloat(priceConfigData['Metro Trade VIP2 AUD Incl']) || 40) / 100;
          
          // Calculate vanity markups (10% more than standard)
          const regionalRetailVanityMarkup = 1 + ((regionalRetailMarkup - 1) + 0.1);
          const metroRetailVanityMarkup = 1 + ((metroRetailMarkup - 1) + 0.1);
          const regionalRetailVipVanityMarkup = 1 + ((regionalRetailVipMarkup - 1) + 0.1);
          const metroRetailVipVanityMarkup = 1 + ((metroRetailVipMarkup - 1) + 0.1);
          
          const configData = {
            // Standard markups
            regional_retail_markup: regionalRetailMarkup,
            regional_trade_markup: regionalTradeMarkup,
            metro_retail_markup: metroRetailMarkup,
            metro_trade_markup: metroTradeMarkup,
            regional_retail_vip_markup: regionalRetailVipMarkup,
            regional_trade_vip1_markup: regionalTradeVip1Markup,
            regional_trade_vip2_markup: regionalTradeVip2Markup,
            metro_retail_vip_markup: metroRetailVipMarkup,
            metro_trade_vip1_markup: metroTradeVip1Markup,
            metro_trade_vip2_markup: metroTradeVip2Markup,
            // Vanity markups (10% more than standard)
            regional_retail_vanity_markup: regionalRetailVanityMarkup,
            metro_retail_vanity_markup: metroRetailVanityMarkup,
            regional_retail_vip_vanity_markup: regionalRetailVipVanityMarkup,
            metro_retail_vip_vanity_markup: metroRetailVipVanityMarkup,
            // Supplier Special markups (same as standard by default)
            regional_retail_supplier_special_markup: regionalRetailMarkup,
            regional_trade_supplier_special_markup: regionalTradeMarkup,
            metro_retail_supplier_special_markup: metroRetailMarkup,
            metro_trade_supplier_special_markup: metroTradeMarkup,
            regional_retail_vip_supplier_special_markup: regionalRetailVipMarkup,
            regional_trade_vip1_supplier_special_markup: regionalTradeVip1Markup,
            regional_trade_vip2_supplier_special_markup: regionalTradeVip2Markup,
            metro_retail_vip_supplier_special_markup: metroRetailVipMarkup,
            metro_trade_vip1_supplier_special_markup: metroTradeVip1Markup,
            metro_trade_vip2_supplier_special_markup: metroTradeVip2Markup,
            // Supplier Special vanity markups (same as vanity by default)
            regional_retail_supplier_special_vanity_markup: regionalRetailVanityMarkup,
            metro_retail_supplier_special_vanity_markup: metroRetailVanityMarkup,
            regional_retail_vip_supplier_special_vanity_markup: regionalRetailVipVanityMarkup,
            metro_retail_vip_supplier_special_vanity_markup: metroRetailVipVanityMarkup
          };
          
          // Apply the pricing configuration
          await supplierService.updateSupplierPricingConfig(createdSupplier.id, configData);
          validationMessage += ` Custom pricing configuration applied.`;
          
        } catch (priceConfigError) {
          console.error('Error applying price configuration:', priceConfigError);
          validationMessage += ' Note: Price configuration upload encountered some issues but supplier was created successfully.';
        }
      }
      
      let uploadSuccessCount = 0;
      let uploadErrorCount = 0;
      let uploadErrorDetails: string[] = [];
      let didUploadInventory = false;

      // Step 2: Upload inventory data if available
      if (validatedInventoryList.length > 0) {
        try {
          // Create a CSV blob from the validated inventory data with proper headers
          // Map the CSV data to the backend expected format
          const backendHeaders = [
            'SKU Code', 'Description', 'Style Code', 'Supplier Name', 'Carton', 
            'Units per carton', 'Carton Dimensions', 'Weight per unit', 
            'Weight per carton', 'No. of units per pallet', 'Pallet weight', 'Available Stock'
          ];
          const csvRows = [];
          
          // Add header row
          csvRows.push(backendHeaders.join(','));
          
          // Add data rows with proper mapping
          validatedInventoryList.forEach(item => {
            const row = [
              `"${item['SKU CODE'] || item['SKU Code'] || ''}"`,           // SKU Code
              `"${item['Description'] || item['PRODUCT DESCRIPTION'] || item['Product Description'] || ''}"`, // Description  
              `"${item['STYLE CODE'] || item['Style Code'] || ''}"`,          // Style Code
              `"${newSupplier.name || ''}"`,                                // Supplier Name
              `"${item['Carton'] || item['CARTON'] || ''}"`,                // Carton
              `"${item['Units per carton'] || item['UNITS PER CARTON'] || ''}"`, // Units per carton
              `"${item['Carton Dimensions'] || item['CARTON DIMENSIONS'] || item['Cart Dimensions'] || ''}"`, // Carton Dimensions
              `"${item['Weight per unit'] || item['WEIGHT PER UNIT'] || ''}"`, // Weight per unit
              `"${item['Weight per carton'] || item['WEIGHT PER CARTON'] || ''}"`, // Weight per carton
              `"${item['No. of units per pallet'] || item['NO. OF UNITS PER PALLET'] || ''}"`, // No. of units per pallet
              `"${item['Pallet weight'] || item['PALLET WEIGHT'] || ''}"`, // Pallet weight
              `"${item['Available Stock'] || item['AVAILABLE STOCK'] || ''}"`, // Available Stock
            ];
            csvRows.push(row.join(','));
          });

          const inventoryCsvContent = csvRows.join('\n');
          const inventoryBlob = new Blob([inventoryCsvContent], { type: 'text/csv' });
          const inventoryFile = new File([inventoryBlob], 'validated_inventory.csv', { type: 'text/csv' });

          // Import the inventory data
          const inventoryResult = await inventoryService.uploadInventoryFile(inventoryFile);
          
          didUploadInventory = true;
          uploadSuccessCount = inventoryResult.success;
          uploadErrorCount = inventoryResult.errors;
          uploadErrorDetails = inventoryResult.error_details || [];
          
          if (inventoryResult.success > 0) {
            validationMessage += ` Successfully imported ${inventoryResult.success} inventory items.`;
            if (inventoryResult.errors > 0) {
              validationMessage += ` ${inventoryResult.errors} inventory items had errors.`;
            }
          }
        } catch (inventoryError) {
          console.error('Error uploading inventory:', inventoryError);
          // Don't fail the entire process if inventory upload fails
          validationMessage += ' Note: Inventory upload encountered some issues but supplier was created successfully.';
        }
      }

      // Step 3: Show success message or results popup
      if (didUploadInventory) {
        setImportResult({
          successCount: uploadSuccessCount,
          errorCount: uploadErrorCount,
          errorDetails: uploadErrorDetails,
          supplierName: createdSupplier.name
        });
        setShowImportResultModal(true);
      } else {
        toast.showToast(`Supplier "${createdSupplier.name}" created successfully! ${validationMessage}`, {
          type: 'success'
        });
      }
      
      // Step 4: Close the modal
      handleCloseModal();
      
      // Step 5: Refresh the suppliers list to show the newly added supplier
      await fetchSuppliers();
      
    } catch (error: any) {
      console.error('Error creating supplier:', error);
      
      // Handle specific error types
      let errorMessage = 'Failed to create supplier. Please try again.';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        if (detail.includes('duplicate') && detail.includes('SKU')) {
          errorMessage = `❌ **Duplicate SKU Error**: Your price list contains duplicate or invalid SKU codes.

**Common Issues:**
• Product descriptions appearing as SKU codes (e.g., "Size: 1500*750*580mm")  
• Duplicate SKU codes in your data
• Non-SKU text being treated as SKU codes

**Solutions:**
• Remove rows that contain product descriptions instead of actual SKU codes
• Ensure each SKU code appears only once
• Valid SKU codes are typically short alphanumeric codes (e.g., "AGV1500GW")

**Example of Valid vs Invalid:**
Valid: "AGV1500GW", "BGV1700MW"  
Invalid: "Size: 1500*750*580mm", "Gloss White Finish"`;
        } else if (detail.includes('name') && detail.includes('already exists')) {
          errorMessage = 'A supplier with this name already exists. Please choose a different name.';
        } else if (detail.includes('phone') && detail.includes('already exists')) {
          errorMessage = 'A supplier with this phone number already exists. Please use a different phone number.';
        } else {
          errorMessage = detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.showToast(errorMessage, { type: 'error' });
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      await generatePriceTemplate();
      toast.showToast('Price template downloaded successfully!', { type: 'success' });
    } catch (error) {
      console.error('Error downloading price template:', error);
      toast.showToast('Failed to generate price template. Please try again.', { type: 'error' });
    }
  };

  const downloadInventoryTemplate = async () => {
    try {
      await generateInventoryTemplate();
      toast.showToast('Inventory template downloaded successfully!', { type: 'success' });
    } catch (error) {
      console.error('Error downloading inventory template:', error);
      toast.showToast('Failed to generate inventory template. Please try again.', { type: 'error' });
    }
  };

  const downloadPriceConfigTemplate = async () => {
    try {
      await generatePriceListConfigurationTemplate();
      toast.showToast('Price configuration template downloaded successfully!', { type: 'success' });
    } catch (error) {
      console.error('Error downloading price configuration template:', error);
      toast.showToast('Failed to generate price configuration template. Please try again.', { type: 'error' });
    }
  };

  // Duplicate SKU Modal Handlers
  const handleCloseDuplicateSkuModal = () => {
    setShowDuplicateSkuModal(false);
    setDuplicateSkus([]);
  };

  const handleFixAndRetry = () => {
    setShowDuplicateSkuModal(false);
    setDuplicateSkus([]);
    // User needs to fix their file and re-upload
    toast.showToast('Please fix the duplicate SKUs in your file and try uploading again.', { type: 'info' });
  };

  // Helper function to render empty state
  const renderEmptyState = () => {
    return (
      <MessageRow>
        No Suppliers  found. Add a new Supplier  to get started.
      </MessageRow>
    );
  };

  // Render grid content with consistent loading pattern
  const renderGridContent = () => {
    if (loading) {
      return (
        <LoadingRow>
          <LoadingContainer>
            <LoadingSpinner size="lg" />
            <LoadingText>Loading suppliers...</LoadingText>
          </LoadingContainer>
        </LoadingRow>
      );
    }

    if (suppliers.length === 0) {
      return renderEmptyState();
    }

    return suppliers.map(supplier => (
      <GridRow key={supplier.id}>
        <div>
          <SupplierNameLink to="#" onClick={(e) => {
            e.preventDefault();
            handleSupplierClick(supplier.id);
          }}>
            {supplier.name}
          </SupplierNameLink>
        </div>
        <div>{supplier.phone}</div>
        <div>{supplier.email}</div>
        <div>{supplier.address}</div>
        <div>{supplier.priceList}</div>
      </GridRow>
    ));
  };

  return (
    <Layout>
      {/* Toast notification */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          errorDetails={toast.errorDetails}
          onClose={toast.hideToast}
        />
      )}

      <PageHeader>
        <HeaderContainer>
          <TitleContainer>
            <StyledBackButton onClick={handleBack}>
              <BackIcon src={backButtonIcon} alt="Back" />
            </StyledBackButton>
            <PageTitle>Suppliers</PageTitle>
          </TitleContainer>
        </HeaderContainer>
      </PageHeader>

      <PageContainer>
        <SearchAddContainer>
          <StyledSearchBar
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search a supplier"
            autoApplyUrlParams={false}
          />
          <AddSupplierButton onClick={handleAddSupplierClick}>
            <img src={customerAddIcon} alt="Add" />
            Add Supplier
          </AddSupplierButton>
        </SearchAddContainer>

        <GridContainer>
          <GridHeader>
            <div>Supplier Name</div>
            <div>Phone No.</div>
            <div>Email</div>
            <div>Address</div>
            <div>Price List</div>
          </GridHeader>

          {renderGridContent()}
          <ScrollHint>
            Tip: If zoomed in, scroll horizontally to view all supplier information
          </ScrollHint>
        </GridContainer>

        {!loading && suppliers.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[5, 10, 25, 50]}
            showItemsPerPage={true}
          />
        )}

        {/* Add Supplier Modal */}
        {showAddModal && (
          <ModalOverlay>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              {isSubmitting && (
                <SubmittingOverlay>
                  <LoadingSpinner size="lg" />
                  <SubmittingText>Uploading files & creating supplier...</SubmittingText>
                </SubmittingOverlay>
              )}
              <ModalHeader>
                <ModalTitle>Add Supplier</ModalTitle>
              </ModalHeader>

              <FormGroup>
                <FormLabel required>Supplier Company Name</FormLabel>
                <FormInput
                  type="text"
                  name="name"
                  value={newSupplier.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Address</FormLabel>
                <FormInput
                  type="text"
                  name="address"
                  value={newSupplier.address}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel required>Phone No</FormLabel>
                <FormInput
                  type="tel"
                  name="phone"
                  value={newSupplier.phone}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Email ID</FormLabel>
                <FormInput
                  type="email"
                  name="email"
                  value={newSupplier.email}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <FormLabelWithTemplate>
                  <FormLabelText required>Inventory</FormLabelText>
                  <TemplateLink href="#" onClick={(e) => {
                    e.preventDefault();
                    downloadInventoryTemplate();
                  }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 2V11M8 11L4.5 7.5M8 11L11.5 7.5M2 13H14" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    InventoryTemplate.xlsx
                  </TemplateLink>
                </FormLabelWithTemplate>
                <FileUploadContainer>
                  <FileInputLabel style={{ border: '1px solid #D1D5DB' }}>
                    <HiddenFileInput
                      type="file"
                      onChange={handleInventoryFileChange}
                      accept=".xlsx,.xls"
                      required
                    />
                    {selectedInventoryFile ? selectedInventoryFile.name : 'Choose Excel File'}
                  </FileInputLabel>
                  <UploadIcon>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 16.5V18.75C3 19.3467 3.23705 19.919 3.65901 20.341C4.08097 20.7629 4.65326 21 5.25 21H18.75C19.3467 21 19.919 20.7629 20.341 20.341C20.7629 19.919 21 19.3467 21 18.75V16.5M16.5 12L12 7.5M12 7.5L7.5 12M12 7.5V16.5" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </UploadIcon>
                </FileUploadContainer>
              </FormGroup>

              <FormGroup>
                <FormLabelWithTemplate>
                  <FormLabelText required>Price List</FormLabelText>
                  <TemplateLink href="#" onClick={(e) => {
                    e.preventDefault();
                    downloadTemplate();
                  }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 2V11M8 11L4.5 7.5M8 11L11.5 7.5M2 13H14" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    PriceTemplate.xlsx
                  </TemplateLink>
                </FormLabelWithTemplate>
                <FileUploadContainer>
                  <FileInputLabel style={{ border: '1px solid #D1D5DB' }}>
                    <HiddenFileInput
                      type="file"
                      onChange={handleFileChange}
                      accept=".xlsx,.xls"
                      required
                    />
                    {selectedFile ? selectedFile.name : 'Choose Excel File'}
                  </FileInputLabel>
                  <UploadIcon>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 16.5V18.75C3 19.3467 3.23705 19.919 3.65901 20.341C4.08097 20.7629 4.65326 21 5.25 21H18.75C19.3467 21 19.919 20.7629 20.341 20.341C20.7629 19.919 21 19.3467 21 18.75V16.5M16.5 12L12 7.5M12 7.5L7.5 12M12 7.5V16.5" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </UploadIcon>
                </FileUploadContainer>
              </FormGroup>

              <FormGroup>
                <FormLabelWithTemplate>
                  <FormLabelText>Price List Configuration (Optional)</FormLabelText>
                  <TemplateLink href="#" onClick={(e) => {
                    e.preventDefault();
                    downloadPriceConfigTemplate();
                  }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 2V11M8 11L4.5 7.5M8 11L11.5 7.5M2 13H14" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    PriceListConfigurationTemplate.xlsx
                  </TemplateLink>
                </FormLabelWithTemplate>
                <FileUploadContainer>
                  <FileInputLabel style={{ border: '1px solid #D1D5DB' }}>
                    <HiddenFileInput
                      type="file"
                      onChange={handlePriceConfigFileChange}
                      accept=".xlsx,.xls"
                    />
                    {selectedPriceConfigFile ? selectedPriceConfigFile.name : 'Choose Excel File'}
                  </FileInputLabel>
                  <UploadIcon>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 16.5V18.75C3 19.3467 3.23705 19.919 3.65901 20.341C4.08097 20.7629 4.65326 21 5.25 21H18.75C19.3467 21 19.919 20.7629 20.341 20.341C20.7629 19.919 21 19.3467 21 18.75V16.5M16.5 12L12 7.5M12 7.5L7.5 12M12 7.5V16.5" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </UploadIcon>
                </FileUploadContainer>
              </FormGroup>

              <ButtonContainer>
                <CancelButton onClick={handleCloseModal}>
                  Cancel
                </CancelButton>
                <SubmitButton
                  onClick={handleSubmit}
                  disabled={!newSupplier.name || !newSupplier.phone || !selectedFile || !selectedInventoryFile}
                >
                  Submit
                </SubmitButton>
              </ButtonContainer>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* Duplicate SKU Modal */}
        <DuplicateSkuModal
          isOpen={showDuplicateSkuModal}
          onClose={handleCloseDuplicateSkuModal}
          duplicateSkus={duplicateSkus}
          onFixAndRetry={handleFixAndRetry}
        />

        {/* Import Result Modal */}
        <ImportResultModal
          isOpen={showImportResultModal}
          onClose={() => setShowImportResultModal(false)}
          successCount={importResult.successCount}
          errorCount={importResult.errorCount}
          errorDetails={importResult.errorDetails}
          supplierName={importResult.supplierName}
        />

        {/* Validation Error Alert Modal */}
        {showValidationErrorModal && (
          <AlertModalOverlay onClick={handleCloseValidationErrorModal}>
            <AlertModalContent onClick={(e) => e.stopPropagation()}>
              <AlertModalTitle>File Validation Error</AlertModalTitle>
              <AlertModalMessage>{validationErrorMessage}</AlertModalMessage>
              <AlertButtonContainer>
                <AlertCancelButton onClick={handleCloseValidationErrorModal}>
                  Cancel
                </AlertCancelButton>
              </AlertButtonContainer>
            </AlertModalContent>
          </AlertModalOverlay>
        )}
      </PageContainer>
    </Layout>
  );
};

export default SuppliersPage;